import { metricNameToMetricId, normalizeMetricGoals } from "./metricGoals"

/** Infer preset focus from goal type display name (fallback when metricGoalTypeId is missing). */
export function inferGoalTypeFocus(name) {
  const n = String(name || "").trim().toLowerCase()
  if (!n) return null
  if (n.includes("awareness") || n.includes("visibility") || n.includes("reach")) return "visibility"
  if (n.includes("balanced")) return "balanced"
  if (n.includes("cross-platform") || n.includes("cross platform")) return "cross_platform"
  if (n.includes("conversion") || n.includes("revenue") || n.includes("roi")) return "roi"
  if (n.includes("growth")) return "growth"
  if (n.includes("engagement")) return "engagement"
  return null
}

function metricIdsFromCatalogForType(typeId, catalog) {
  const goalTypeId = Number(typeId)
  if (!Number.isFinite(goalTypeId)) return []
  const seen = new Set()
  const ids = []
  for (const row of catalog) {
    if (Number(row.metricGoalTypeId) !== goalTypeId) continue
    const id = metricNameToMetricId(row.metricName)
    if (!id || seen.has(id)) continue
    seen.add(id)
    ids.push(id)
  }
  return ids
}

function metricNamesToIds(metricNames, availableIds) {
  const set = availableIds instanceof Set ? availableIds : new Set(availableIds || [])
  return (metricNames || [])
    .map((name) => metricNameToMetricId(name))
    .filter((id) => id && set.has(id))
}

function focusFromCatalogForType(typeId, catalog, typeName) {
  const rows = catalog.filter((row) => Number(row.metricGoalTypeId) === Number(typeId))
  if (rows.length === 0) return inferGoalTypeFocus(typeName)
  const categories = new Set(rows.map((r) => r.categoryType))
  if (categories.size === 1) {
    const cat = rows[0].categoryType
    if (cat === "Growth") return "growth"
    if (cat === "Engagement") return "engagement"
    if (cat === "Visibility") return "visibility"
    if (cat === "Conversions & Revenue") return "roi"
  }
  return inferGoalTypeFocus(typeName)
}

export function normalizeMetricGoalTypes(data) {
  const list = Array.isArray(data)
    ? data
    : Array.isArray(data?.data)
      ? data.data
      : []

  return list
    .filter((item) => item?.deletedAt == null)
    .map((item) => ({
      id: Number(item.id),
      name: item.name ?? "",
      description: item.description ?? "",
      metricNames: item.metricNames ?? item.metrics ?? null,
      insertedDatetime: item.insertedDatetime ?? null,
      updatedDatetime: item.updatedDatetime ?? null,
      deletedAt: item.deletedAt ?? null,
    }))
    .filter((item) => Number.isFinite(item.id) && item.name)
    .sort((a, b) => a.id - b.id)
}

/** Merge API goal types with metric catalog from GET /metric-goals (metricGoalTypeId links). */
export function buildGoalPresetsFromApiTypes(types, metricGoalsCatalog = []) {
  const list = normalizeMetricGoalTypes(types)
  if (list.length === 0) return []

  const catalog = normalizeMetricGoals(metricGoalsCatalog)
  const availableMetricIds = new Set(
    catalog.map((row) => metricNameToMetricId(row.metricName)).filter(Boolean),
  )

  return list.map((type) => {
    let metricIds = metricIdsFromCatalogForType(type.id, catalog).filter((id) =>
      availableMetricIds.has(id),
    )
    if (metricIds.length === 0) {
      const rawNames = type.metricNames
      if (Array.isArray(rawNames) && rawNames.length > 0) {
        const names = rawNames
          .map((entry) => {
            if (typeof entry === "string") return entry
            return entry?.metricName ?? entry?.name ?? ""
          })
          .filter(Boolean)
        metricIds = metricNamesToIds(names, availableMetricIds)
      }
    }
    const focus = focusFromCatalogForType(type.id, catalog, type.name)
    return {
      id: type.id,
      name: type.name,
      description: type.description || "",
      metricIds,
      focus,
    }
  })
}

export function resolveGoalPresets(types, metricGoalsCatalog = []) {
  return buildGoalPresetsFromApiTypes(types, metricGoalsCatalog)
}
